import { useState, useRef } from 'preact/hooks';
import { ImageCard } from '../Image/ImageCard';
import { ImageViewer } from '../Image/ImageViewer';
import { usePullToRefresh } from '../../hooks/usePullToRefresh';
import type { ChibiImage } from '../../types';

interface FeedScrollProps {
  images: ChibiImage[];
}

export function FeedScroll({ images }: FeedScrollProps) {
  const [selectedImage, setSelectedImage] = useState<ChibiImage | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  
  const handleRefresh = async () => {
    // Simulate refresh - in real app, this would reload the manifest
    console.log('Refreshing feed...');
    await new Promise(resolve => setTimeout(resolve, 1500));
    // Could trigger manifest reload here
    window.location.reload();
  };

  const { isPulling, pullDistance, isRefreshing, pullProgress } = usePullToRefresh({
    onRefresh: handleRefresh,
    container: containerRef.current,
  });

  return (
    <>
      <div 
        ref={containerRef}
        class="max-w-2xl mx-auto h-full overflow-y-auto relative"
        style={{ transform: isPulling || isRefreshing ? `translateY(${pullDistance}px)` : 'translateY(0)', transition: isPulling ? 'none' : 'transform 0.3s' }}
      >
        {/* Pull to refresh indicator */}
        <div 
          class="absolute -top-12 left-1/2 -translate-x-1/2 flex items-center justify-center"
          style={{ 
            opacity: pullProgress,
            transform: `scale(${pullProgress})`,
          }}
        >
          <div class={`w-10 h-10 rounded-full bg-soft-blue flex items-center justify-center ${isRefreshing ? 'animate-spin' : ''}`}>
            <svg class="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          </div>
        </div>

        <div class="p-4 pb-20 space-y-6">
          {images.map((image) => (
            <ImageCard
              key={image.id}
              image={image}
              onClick={() => setSelectedImage(image)}
              layout="feed"
            />
          ))}
        </div>
      </div>

      {selectedImage && (
        <ImageViewer
          image={selectedImage}
          isOpen={!!selectedImage}
          onClose={() => setSelectedImage(null)}
        />
      )}
    </>
  );
}